import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { Check, ChevronDown, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const DEMO_SEAL = "MIRA-3EGB";

const TEXTS = {
  de: {
    kicker: "Das Mira-Siegel",
    title: "Trau keinem Filter. Trau dem Siegel.",
    copy: "Jedes geteilte Mira-Ergebnis trägt eine einmalige Siegel-Nummer und einen Prüf-QR. Wer scannt, sieht das unbearbeitete Original – 90 Tage lang.",
    bullets: [
      "Nicht behauptet, sondern prüfbar – das erste verifizierbare Ehrlichkeits-Siegel",
      "LIVE-Merkmal für Kamera-Aufnahmen: live vor dem Spiegel, kein altes Galerie-Foto",
      "Privatsphäre inklusive: prüfbar nur, wenn du es beim Teilen aktivierst – und nur 90 Tage",
    ],
    stepsTitle: "So funktioniert's (3 Schritte)",
    steps: [
      { t: "Teilen", x: "Beim Teilen erhält die Karte automatisch ihre Siegel-Nr. (z. B. Siegel MIRA-7F3K) und den Prüf-QR." },
      { t: "Scannen", x: "Jeder Betrachter kann den QR scannen – ganz ohne App." },
      { t: "Vergleichen", x: "Die Prüf-Seite zeigt das Original, wie Mira es analysiert hat – mit Datum und LIVE-Merkmal. Jede Abweichung zum Post bedeutet: nachträglich bearbeitet." },
    ],
    faq: [
      { q: "Was sieht jemand, der scannt?", a: "Das unbearbeitete Original-Ergebnis (Foto, Wirkungs-Stufe, Datum), wie es bei der Analyse entstanden ist – sonst nichts." },
      { q: "Kann ich das abschalten?", a: "Ja. Der Schalter «Original prüfbar machen» erscheint bei jedem Teilen – du entscheidest pro Bild." },
      { q: "Was passiert nach 90 Tagen?", a: "Die Prüf-Seite verfällt automatisch. Das Original bleibt privat in deiner Chronik." },
    ],
    sealLabel: "Siegel",
    sealStd: "Ehrlich analysiert · Kein Filter",
    liveChip: "LIVE-ANALYSE",
    tryIt: "Probier's aus: scanne dieses Siegel",
    openDemo: "Prüf-Seite ansehen",
    fineprint: "Die Mira-Analyse ist eine automatisierte, unverbindliche KI-Stileinschätzung ohne Gewähr. Das Siegel bestätigt ausschliesslich das bei der Analyse erzeugte Original. Für nachträgliche Bearbeitungen, Vervielfältigungen oder Veröffentlichungen geteilter Bilder durch Nutzerinnen oder Dritte sowie für Entscheidungen auf Basis dieser Einschätzung übernimmt Mira keine Haftung.",
  },
  en: {
    kicker: "The Mira Seal",
    title: "Don't trust a filter. Trust the seal.",
    copy: "Every shared Mira result carries a unique seal number and a verification QR. Anyone who scans it sees the unedited original – for 90 days.",
    bullets: [
      "Not claimed, but verifiable – the first verifiable honesty seal",
      "LIVE marker for camera shots: live in front of the mirror, no old gallery photo",
      "Privacy included: verifiable only if you enable it when sharing – and only for 90 days",
    ],
    stepsTitle: "How it works (3 steps)",
    steps: [
      { t: "Share", x: "When sharing, the card automatically receives its seal number (e.g. Seal MIRA-7F3K) and the verification QR." },
      { t: "Scan", x: "Any viewer can scan the QR – no app required." },
      { t: "Compare", x: "The verification page shows the original as Mira analysed it – with date and LIVE marker. Any difference from the post means: edited afterwards." },
    ],
    faq: [
      { q: "What does someone who scans see?", a: "The unedited original result (photo, presence level, date) as created at the time of analysis – nothing else." },
      { q: "Can I turn it off?", a: "Yes. The \u201CMake original verifiable\u201D switch appears every time you share – you decide per image." },
      { q: "What happens after 90 days?", a: "The verification page expires automatically. Your original stays private in your journal." },
    ],
    sealLabel: "Seal",
    sealStd: "Honestly analysed · No filter",
    liveChip: "LIVE ANALYSIS",
    tryIt: "Try it: scan this seal",
    openDemo: "View verification page",
    fineprint: "The Mira analysis is an automated, non-binding AI style assessment without warranty. The seal only confirms the original created at the time of analysis. Mira accepts no liability for subsequent edits, reproductions or publications of shared images by users or third parties, nor for decisions based on this assessment.",
  },
  es: {
    kicker: "El sello Mira",
    title: "No confíes en un filtro. Confía en el sello.",
    copy: "Cada resultado compartido de Mira lleva un número de sello único y un QR de verificación. Quien lo escanea ve el original sin editar – durante 90 días.",
    bullets: [
      "No afirmado, sino verificable – el primer sello de honestidad verificable",
      "Marca LIVE para fotos con cámara: en vivo frente al espejo, sin fotos antiguas de la galería",
      "Privacidad incluida: verificable solo si lo activas al compartir – y solo 90 días",
    ],
    stepsTitle: "Cómo funciona (3 pasos)",
    steps: [
      { t: "Compartir", x: "Al compartir, la tarjeta recibe automáticamente su número de sello (p. ej. Sello MIRA-7F3K) y el QR de verificación." },
      { t: "Escanear", x: "Cualquiera puede escanear el QR – sin necesidad de app." },
      { t: "Comparar", x: "La página de verificación muestra el original tal como Mira lo analizó – con fecha y marca LIVE. Cualquier diferencia con el post significa: editado después." },
    ],
    faq: [
      { q: "¿Qué ve quien escanea?", a: "El resultado original sin editar (foto, nivel de efecto, fecha) tal como se creó en el análisis – nada más." },
      { q: "¿Puedo desactivarlo?", a: "Sí. El interruptor «Hacer verificable el original» aparece cada vez que compartes – tú decides por imagen." },
      { q: "¿Qué pasa tras 90 días?", a: "La página de verificación caduca automáticamente. Tu original queda privado en tu crónica." },
    ],
    sealLabel: "Sello",
    sealStd: "Analizado con honestidad · Sin filtro",
    liveChip: "ANÁLISIS LIVE",
    tryIt: "Pruébalo: escanea este sello",
    openDemo: "Ver página de verificación",
    fineprint: "El análisis de Mira es una evaluación de estilo automatizada y no vinculante, sin garantía. El sello confirma únicamente el original creado en el análisis. Mira no asume responsabilidad por ediciones, reproducciones o publicaciones posteriores de imágenes compartidas por usuarios o terceros, ni por decisiones basadas en esta evaluación.",
  },
  it: {
    kicker: "Il sigillo Mira",
    title: "Non fidarti di un filtro. Fidati del sigillo.",
    copy: "Ogni risultato Mira condiviso porta un numero di sigillo unico e un QR di verifica. Chi lo scansiona vede l'originale non modificato – per 90 giorni.",
    bullets: [
      "Non dichiarato, ma verificabile – il primo sigillo di onestà verificabile",
      "Contrassegno LIVE per gli scatti con fotocamera: dal vivo davanti allo specchio, nessuna vecchia foto della galleria",
      "Privacy inclusa: verificabile solo se lo attivi quando condividi – e solo per 90 giorni",
    ],
    stepsTitle: "Come funziona (3 passi)",
    steps: [
      { t: "Condividi", x: "Alla condivisione la card riceve automaticamente il suo numero di sigillo (es. Sigillo MIRA-7F3K) e il QR di verifica." },
      { t: "Scansiona", x: "Chiunque può scansionare il QR – senza app." },
      { t: "Confronta", x: "La pagina di verifica mostra l'originale come Mira l'ha analizzato – con data e contrassegno LIVE. Ogni differenza dal post significa: modificato dopo." },
    ],
    faq: [
      { q: "Cosa vede chi scansiona?", a: "Il risultato originale non modificato (foto, livello di splendore, data) come creato all'analisi – nient'altro." },
      { q: "Posso disattivarlo?", a: "Sì. L'interruttore «Rendi verificabile l'originale» appare a ogni condivisione – decidi tu per ogni immagine." },
      { q: "Cosa succede dopo 90 giorni?", a: "La pagina di verifica scade automaticamente. Il tuo originale resta privato nel tuo diario." },
    ],
    sealLabel: "Sigillo",
    sealStd: "Analisi onesta · Nessun filtro",
    liveChip: "ANALISI LIVE",
    tryIt: "Provalo: scansiona questo sigillo",
    openDemo: "Vedi la pagina di verifica",
    fineprint: "L'analisi Mira è una valutazione di stile automatizzata e non vincolante, senza garanzia. Il sigillo conferma esclusivamente l'originale creato all'analisi. Mira non si assume responsabilità per modifiche, riproduzioni o pubblicazioni successive di immagini condivise da utenti o terzi, né per decisioni basate su questa valutazione.",
  },
  fr: {
    kicker: "Le sceau Mira",
    title: "Ne crois pas un filtre. Crois le sceau.",
    copy: "Chaque résultat Mira partagé porte un numéro de sceau unique et un QR de vérification. Qui le scanne voit l'original non retouché – pendant 90 jours.",
    bullets: [
      "Pas affirmé, mais vérifiable – le premier sceau d'honnêteté vérifiable",
      "Marque LIVE pour les prises caméra : en direct devant le miroir, pas d'ancienne photo de la galerie",
      "Vie privée incluse : vérifiable seulement si tu l'actives au partage – et seulement 90 jours",
    ],
    stepsTitle: "Comment ça marche (3 étapes)",
    steps: [
      { t: "Partager", x: "Au partage, la carte reçoit automatiquement son numéro de sceau (p. ex. Sceau MIRA-7F3K) et le QR de vérification." },
      { t: "Scanner", x: "Tout spectateur peut scanner le QR – sans app." },
      { t: "Comparer", x: "La page de vérification montre l'original tel que Mira l'a analysé – avec date et marque LIVE. Toute différence avec le post signifie : retouché après coup." },
    ],
    faq: [
      { q: "Que voit la personne qui scanne ?", a: "Le résultat original non retouché (photo, niveau d'éclat, date) tel que créé à l'analyse – rien d'autre." },
      { q: "Puis-je le désactiver ?", a: "Oui. L'interrupteur « Rendre l'original vérifiable » apparaît à chaque partage – tu décides par image." },
      { q: "Que se passe-t-il après 90 jours ?", a: "La page de vérification expire automatiquement. Ton original reste privé dans ta chronique." },
    ],
    sealLabel: "Sceau",
    sealStd: "Analysé avec honnêteté · Sans filtre",
    liveChip: "ANALYSE LIVE",
    tryIt: "Essaie : scanne ce sceau",
    openDemo: "Voir la page de vérification",
    fineprint: "L'analyse Mira est une évaluation de style automatisée et non contraignante, sans garantie. Le sceau confirme uniquement l'original créé lors de l'analyse. Mira décline toute responsabilité pour les modifications, reproductions ou publications ultérieures d'images partagées par des utilisateurs ou des tiers, ainsi que pour les décisions fondées sur cette évaluation.",
  },
};

export default function MiraSeal() {
  const { lang } = useLang();
  const t = TEXTS[lang] || TEXTS.de;
  const [open, setOpen] = useState(-1);
  const verifyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v/${DEMO_SEAL}?lang=${lang}`;

  return (
    <section id="mira-siegel" className="py-24 sm:py-32" data-testid="mira-seal-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> {t.kicker}
            </p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="mira-seal-title">
              {t.title}
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{t.copy}</p>
            <ul className="mt-7 space-y-3.5">
              {t.bullets.map((x, i) => (
                <li key={i} className="flex items-start gap-3 text-base" data-testid={`seal-bullet-${i}`}>
                  <Check size={17} className="mt-1 shrink-0" style={{ color: "var(--camel)" }} />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mx-auto max-w-sm">
              <div className="rounded-3xl border-2 p-6 shadow-xl -rotate-1 transition-transform duration-500 hover:rotate-0" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="seal-demo-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Star size={15} />
                    <span className="text-[11px] font-black uppercase" style={{ letterSpacing: "0.16em", color: "var(--camel)" }}>Mira Mirror</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-black uppercase text-white" style={{ background: "#C0392B", letterSpacing: "0.12em" }}>
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {t.liveChip}
                  </span>
                </div>
                <p className="mt-4 font-serif-display text-lg font-semibold leading-snug">✦ {t.sealStd}</p>
                <div className="mt-5 flex items-center gap-5 rounded-2xl border p-4" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
                  <div className="rounded-xl bg-white p-2 shadow-sm">
                    <QRCodeSVG value={verifyUrl} size={88} fgColor="#2C2725" bgColor="#FFFFFF" data-testid="seal-demo-qr" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-bold" style={{ letterSpacing: "0.14em", color: "var(--muted-warm)" }}>{t.sealLabel}-Nr.</p>
                    <p className="font-mono text-lg font-bold tracking-wider" data-testid="seal-demo-number">{DEMO_SEAL}</p>
                    <p className="mt-1.5 text-[11px] leading-snug" style={{ color: "var(--muted-warm)" }}>{t.tryIt}</p>
                  </div>
                </div>
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="seal-demo-link"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold gold-underline"
                  style={{ color: "var(--camel)" }}
                >
                  {t.openDemo} <ExternalLink size={13} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-16">
          <h3 className="font-serif-display text-2xl font-semibold" data-testid="seal-steps-title">{t.stepsTitle}</h3>
          <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {t.steps.map((s, i) => (
              <div key={i} className="rounded-3xl border p-6" style={{ borderColor: "var(--line)", background: "var(--surface)" }} data-testid={`seal-step-${i}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full font-serif-display text-lg font-bold text-white" style={{ background: "var(--camel)" }}>{i + 1}</span>
                <p className="mt-4 font-semibold">{s.t}</p>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{s.x}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-14 mx-auto max-w-3xl">
          <div className="divide-y rounded-3xl border" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
            {t.faq.map((f, i) => (
              <div key={i} style={{ borderColor: "var(--line)" }}>
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`seal-faq-${i}`}
                  className="flex w-full items-center justify-between gap-4 px-6 py-4.5 text-left text-sm sm:text-base font-semibold"
                  style={{ padding: "18px 24px" }}
                >
                  {f.q}
                  <ChevronDown size={17} className={`shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} style={{ color: "var(--camel)" }} />
                </button>
                {open === i && (
                  <p className="px-6 pb-5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }} data-testid={`seal-faq-answer-${i}`}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-6 text-[11px] leading-relaxed" style={{ color: "var(--muted-warm)" }} data-testid="seal-fineprint">{t.fineprint}</p>
        </Reveal>
      </div>
    </section>
  );
}
